import { warn } from "@swivl-mono/helpers";
import { EscalationInterface, EscalationStatus } from "@swivl-mono/types";
import { FiltersDropdown, FiltersInterface, SSColumnHeader } from "@swivl-mono/ui";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { Link, useMatch } from "react-router-dom";

import { PageLoadingView } from "../../../App/Layout/Loading/LoadingView";
import { renderBlankState, renderLoadingColumn } from "../../../Components/BlankStates/ColumnStates";
import { InfiniteScrollView } from "../../../Components/InfiniteScrollView/InfiniteScrollView";
import { NavigationHelper } from "../../../Helpers/Navigation/Navigation.Helper";
import EscalationsModel from "../../../Models/Escalations/Escalations.Model";
import { NavBar } from "../../../Models/NavBar/NavBar.Model";
import { EscalationHistoryDetails } from "./Details/EscalationHistoryDetails";
import "./EscalationHistoryView.scss";
import MembersModel from "../../../Models/Member/Members.Model";

function TextForEscalationStatus(escalation: EscalationInterface, status: string) {
  switch (status) {
    case EscalationStatus.PENDING:
      return "Created";
    case EscalationStatus.ACTIVE:
      return "Looking for Agent";
    case EscalationStatus.ASSIGNED:
      if (escalation.assignedMember) {
        return `Assigned to ${escalation.assignedMember.agentName}`;
      }
      return "Assigned";
    case EscalationStatus.HANDLED:
      if (escalation.assignedMember) {
        return `Handled by ${escalation.assignedMember.agentName}`;
      }
      return `Handled`;
    case EscalationStatus.TIMED_OUT:
      return "No Agents Accepted";
    case EscalationStatus.NO_AGENT_AVAILABLE:
      return "No Agents Available";
    case EscalationStatus.OUTSIDE_HOURS:
      return "Outside Hours";
  }
}

export default function EscalationHistoryView() {

  //TODO - Devin
  const { escalations, isSearching, isLoading, escalationSearchResults, escalationsPage, escalationsSearchPage } = EscalationsModel.useState();
  const { members } = MembersModel.useState();
  const [searchQuery, setSearchQuery] = useState("");

  const [filters, setFilters] = useState<FiltersInterface[]>([]);

  const isFiltered = useMemo(() => {
    if (searchQuery.length) return true;
    return filters.some((f) => f.filters.some((f) => f.on));
  }, [filters, searchQuery]);

  const search = (_searchQuery, _filters, page) => {
    // Helper function to DRY up the code
    EscalationsModel.searchForEscalations(
      _searchQuery,
      page,
      _filters[0].filters.filter((f) => f.on).map((f) => f.id) as any,
      _filters[1].filters.filter((f) => f.on).map((f) => f.id) as any,
    );
  };

  useEffect(() => {
    if (isFiltered || searchQuery.length) {
      search(searchQuery, filters, 1);
    }
  }, [isFiltered, filters, searchQuery]);

  const resetFilters = () => {
    setFilters([
      {
        id: "result",
        title: "Result",
        filters: [
          { id: EscalationStatus.HANDLED, name: "Handled", on: false, disabled: false },
          { id: EscalationStatus.TIMED_OUT, name: "No Agents Accepted", on: false, disabled: false },
          { id: EscalationStatus.OUTSIDE_HOURS, name: "Outside Hours", on: false, disabled: false },
          { id: EscalationStatus.NO_AGENT_AVAILABLE, name: "No Agent Available", on: false, disabled: false },
        ],
      },
      {
        id: "agents",
        title: "Agents",
        filters: members && members.length > 0
          ? members.sort((a, b) => {
            if (a.agentName && b.agentName) {
              return a.agentName < b.agentName ? -1 : a.agentName > b.agentName ? 1 : 0;
            }
            return 0;
          })
            .map((agent) => {
              return { id: agent.id, name: agent.agentName, on: false, disabled: false };
            }) as any : []
      }
    ]);
  };

  useEffect(() => {
    if (members && members.length && filters.length === 0) {
      resetFilters();
    }
  }, [members]);

  const match = useMatch("/settings/escalation-history/:id");

  const escalationsToDisplay = isFiltered ? escalationSearchResults : escalations;
  const escalationId = match?.params?.id ? parseInt(match.params.id) : null;

  const selectedEscalation = useMemo(() => {
    if (escalationsToDisplay && escalationId) {
      return escalationsToDisplay.find((e) => e.id === escalationId);
    }
  }, [escalations, match, escalationsToDisplay]);

  useEffect(() => {
    if (escalations && match && match.params && match.params.id && !selectedEscalation) {
      warn("ESCALATION IS NOT HERE!");
      NavigationHelper.navigate("/settings/escalation-history");
    }
  }, [selectedEscalation, escalations]);

  useEffect(() => {
    EscalationsModel.loadAllEscalations();
    MembersModel.loadMembers();

    NavBar.set(<NavBar.Component.Back to="/settings" />, <NavBar.Component.Title title="Escalation History" />, null, "Escalation History");
  }, []);

  const isLoadingMore = isFiltered ? (escalationsSearchPage > 1 && isSearching ? true : false) : escalationsPage > 1 && isLoading ? true : false;
  const shouldLoadMore = isFiltered
    ? escalationSearchResults && escalationSearchResults.length >= EscalationsModel.ResultsPerPage * escalationsSearchPage
    : escalations && escalations.length >= EscalationsModel.ResultsPerPage * escalationsPage;

  return (
    <div className="EscalationHistoryView">
      <div className="column usersColoumn">
        <SSColumnHeader
          filters={[]}
          customFilters={
            <FiltersDropdown
              filters={filters}
              resetFilters={() => {
                resetFilters();
              }}
              saveFilters={(filters) => {
                setFilters(filters);
              }}
            />
          }
          title={undefined}
          selectedFilter={""}
          filterSelected={(f: any) => { }}
          searchUpdated={setSearchQuery}
          searchQuery={searchQuery}
          analytics={{ category: "Live" }}
        />

        <InfiniteScrollView
          isLoadingMore={isLoadingMore}
          shouldLoadMore={shouldLoadMore}
          loadMore={() => {
            if (isFiltered) {
              search(searchQuery, filters, escalationsSearchPage + 1);
            } else {
              EscalationsModel.loadNextEscalationsPage();
            }
          }}
        >
          {isFiltered && isSearching && !isFiltered
            ? renderLoadingColumn()
            : escalationsToDisplay && escalationsToDisplay.length > 0
              ? escalationsToDisplay.map((e) => {
                return (
                  <Link
                    to={"/settings/escalation-history/" + e.id}
                    className={escalationId === e.id ? "escalationHistoryItem selected" : "escalationHistoryItem"}
                    key={e.id}
                  >
                    <div className="name">{e.user?.name}</div>
                    <div className="status">{TextForEscalationStatus(e, e.status)}</div>
                    <div className="createdAt">Created {DateTime.fromISO(e.createdAt as unknown as string).toFormat("L/d/yy @ t")}</div>
                  </Link>
                );
              })
              : escalationsToDisplay && !isSearching
                ? renderBlankState(null, "No Results")
                : renderLoadingColumn()}
        </InfiniteScrollView>
      </div>
      <div className="column">
        {selectedEscalation ? <EscalationHistoryDetails escalation={selectedEscalation} /> : match?.params?.id ? <PageLoadingView /> : null}
      </div>
    </div>
  );

  // }
}
